import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import {
  CreateOrderRequest,
  CreateSaleRequest,
  Order,
  PurchaseVoucher,
  SaleInvoice,
} from '../models/product.models';
import { PagedResponse } from '../models/user.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private readonly api = inject(ApiService);

  list(): Observable<Order[]> {
    return this.api
      .get<ApiResponse<Order[] | PagedResponse<Order>> | Order[] | PagedResponse<Order>>(
        API_ENDPOINTS.orders.me,
        undefined,
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) =>
          Array.isArray(response)
            ? response
            : ((response as PagedResponse<Order> & { orders?: Order[] }).orders ??
              response.items ??
              response.data ??
              response.results ??
              []),
        ),
      );
  }

  sellNewItem(payload: CreateOrderRequest): Observable<Order> {
    return this.api
      .post<ApiResponse<Order> | Order, CreateOrderRequest>(
        API_ENDPOINTS.orders.base,
        payload,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  createSale(payload: CreateSaleRequest): Observable<Order> {
    return this.api
      .post<ApiResponse<Order | { order: Order; invoiceEmailSent?: boolean }> | Order | { order: Order; invoiceEmailSent?: boolean }, CreateSaleRequest>(
        API_ENDPOINTS.orders.sales,
        payload,
        'marketplace',
        60_000,
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ('order' in response
          ? { ...response.order, invoiceEmailSent: response.invoiceEmailSent }
          : response)),
      );
  }

  createPayUDynamicQr(payload: {
    orderId: string; amount: number; currency: string; customerName: string;
    customerEmail: string; customerPhone: string; productInfo: string;
    successUrl: string; failureUrl: string; expiryMinutes: number;
  }): Observable<{ accepted: boolean; message: string; providerReference?: string; qrString?: string; paymentId?: string; status?: string }> {
    return this.api
      .post<ApiResponse<any> | any, typeof payload>(
        '/third-party/payments/payu/create', payload, 'marketplace', 60_000,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  sales(): Observable<Order[]> {
    return this.api
      .get<ApiResponse<Order[] | { orders: Order[] }> | Order[] | { orders: Order[] }>(
        API_ENDPOINTS.orders.mySales,
        undefined,
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => (Array.isArray(response) ? response : (response.orders ?? []))),
      );
  }

  saleInvoice(orderId: string): Observable<SaleInvoice> {
    return this.api
      .get<ApiResponse<SaleInvoice> | SaleInvoice>(
        `${API_ENDPOINTS.orders.base}/${orderId}/sale-invoice`,
        undefined,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  updateStatus(orderId: string, status: string): Observable<Order> {
    return this.api
      .put<ApiResponse<Order | { order: Order }> | Order | { order: Order }, { status: string }>(
        `${API_ENDPOINTS.orders.base}/${orderId}/status`,
        { status },
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => ('order' in response ? response.order : response)),
      );
  }

  purchaseVoucher(orderId: string): Observable<PurchaseVoucher> {
    return this.api
      .get<ApiResponse<PurchaseVoucher> | PurchaseVoucher>(
        `${API_ENDPOINTS.orders.base}/${orderId}/purchase-voucher`,
        undefined,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  suppliers(): Observable<any[]> {
    return this.api
      .get<ApiResponse<any[]> | any[]>('/purchases/suppliers', undefined, 'marketplace')
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  createSupplier(payload: Record<string, string>): Observable<any> {
    return this.api
      .post<ApiResponse<any> | any, Record<string, string>>(
        '/purchases/suppliers',
        payload,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  purchases(): Observable<any[]> {
    return this.api
      .get<ApiResponse<any[] | Record<string, unknown>> | any[] | Record<string, unknown>>(
        '/purchases',
        undefined,
        'marketplace',
      )
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => this.extractPurchases(response)),
      );
  }

  inventory(params: {
    status: string;
    search?: string;
    condition?: string;
    sort?: string;
    page: number;
    pageSize: number;
  }): Observable<any> {
    return this.api
      .get<ApiResponse<any> | any>('/purchases/inventory', params, 'marketplace')
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  purchaseCatalogItems(search: string): Observable<any[]> {
    return this.api
      .get<ApiResponse<any> | any>('/purchases/catalog-items', { search, take: 12 }, 'marketplace')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => Array.isArray(response) ? response : (response?.items ?? [])),
      );
  }

  saleInventory(search = ''): Observable<any[]> {
    return this.api
      .get<ApiResponse<any> | any>('/purchases/sale-inventory', { search, take: 100 }, 'marketplace')
      .pipe(
        map((response) => unwrapApiResponse(response)),
        map((response) => Array.isArray(response) ? response : (response?.items ?? [])),
      );
  }

  createPurchase(payload: FormData): Observable<any> {
    return this.api
      .post<ApiResponse<any> | any, FormData>('/purchases', payload, 'marketplace', 60_000)
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  generatePurchaseVoucher(purchaseId: string): Observable<any> {
    return this.api
      .post<ApiResponse<any> | any, Record<string, never>>(
        `/purchases/${purchaseId}/voucher`,
        {},
        'marketplace',
        60_000,
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  linkPurchaseItem(itemId: string, productId: string): Observable<void> {
    return this.api
      .put<ApiResponse<unknown> | unknown, { productId: string }>(
        `/purchases/items/${itemId}/product`,
        { productId },
        'marketplace',
      )
      .pipe(map(() => undefined));
  }

  setInventorySaleReadiness(itemId: string, enabled: boolean, maxDiscountPercent = 0, categoryId?: string): Observable<any> {
    return this.api
      .put<ApiResponse<any> | any, { enabled: boolean; maxDiscountPercent: number; categoryId?: string }>(
        `/purchases/items/${itemId}/sale-readiness`,
        { enabled, maxDiscountPercent, categoryId },
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response)));
  }

  private extractPurchases(value: unknown, depth = 0): any[] {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== 'object' || depth > 3) return [];

    const container = value as Record<string, unknown>;
    for (const key of ['purchases', 'items', 'results', 'data']) {
      if (!(key in container)) continue;
      const result = this.extractPurchases(container[key], depth + 1);
      if (result.length || Array.isArray(container[key])) return result;
    }
    return [];
  }
}
